import { useEffect, useRef } from 'preact/hooks';
import { useInstallPrompt } from '../hooks/useInstallPrompt.js';
import { usePromptSlotBusy } from '../shell/promptSlot.js';

interface InstallBannerProps {
  /** The shell's own `showNav`. The route rules live in `main.tsx`, not restated here. */
  navVisible: boolean;
  /**
   * 139: reports this bar's measured height to the shell, which folds it into `--shell-chrome-h`.
   * Reports `0` when the offer is hidden and on unmount, so the space is released. 138 published
   * its own `--install-offer-h`; a second property would have re-created the several-sources-of-
   * truth problem 139 exists to remove.
   */
  onHeightChange: (px: number) => void;
}

/**
 * 138 US2 — offers the app for installation: a one-tap install on Chromium, and on iOS the manual
 * Share → Add to Home Screen steps, which are the only route Apple provides (research.md R2).
 *
 * 🔴 `fixed`, not a flow child of the shared top banner slot. `UpdateToast` and
 * `ImpersonationBanner` sit in that slot and each adds its own height to the page beneath — 40px,
 * measured live, which is exactly enough to give a screen that otherwise fits a band of empty
 * scroll room. That is the defect 134, 136 and 137 spent three features removing, and a second
 * flow banner would have reintroduced it on every screen (research.md R4, FR-014). Out of flow, it
 * costs nothing.
 *
 * `z-30` is deliberate and deliberately low. `BottomNav` is `z-50` and the date/time picker's own
 * panel is `z-[60]` precisely to out-rank it (`BottomNav.tsx:29-31`) — but the rank that actually
 * decides this is `PickerPanel.tsx:119`, the picker's full-screen scrim, which is `z-40`. At an
 * equal rank this bar wins on DOM order (the shell renders it after the routed content), so it
 * floated over the dimmer of an open modal and stayed tappable. Below the scrim is the only
 * correct rank.
 *
 * 🔴 Being out of flow is not the same as being harmless. Measured live on `/account/bookings`
 * with the list scrolled to its end: this bar's box was 502–587 and the pager's Next button
 * 531–563, and `elementFromPoint` at that button's centre returned this component — the control
 * did nothing when tapped. That is 078's defect exactly. So the bar measures itself and reports
 * that height to the shell (139: it published `--install-offer-h` directly until the shell took
 * over owning the single figure), and every screen that bounds itself to the shell's leftovers
 * subtracts it. FR-014 holds in its real sense: the page never gains EMPTY scroll room, and never
 * more than this bar's own height, only while it is shown.
 */
export function InstallBanner({ navVisible, onHeightChange }: InstallBannerProps) {
  const { state, promptInstall, dismiss } = useInstallPrompt();
  // 389 (research R8): steps aside while the home pop-up is open, so only one prompt is on screen.
  const promptOpen = usePromptSlotBusy();
  const hidden = state === 'hidden' || promptOpen;
  const ref = useRef<HTMLDivElement>(null);

  // Declared before any early return: hooks must run in the same order on every render, and the
  // hidden state returns null below.
  //
  // `hidden` and `navVisible` are re-run triggers, not values read inside: the bar mounts and
  // unmounts as the offer appears and disappears, and moves between `bottom-20` and `bottom-0`
  // between routes. Without them the effect would keep a stale measurement — or none at all, since
  // `ref.current` is null on the render where the offer is still hidden. `onHeightChange` IS read,
  // and is listed for that reason.
  // biome-ignore lint/correctness/useExhaustiveDependencies: hidden/navVisible are re-run triggers — see above
  useEffect(() => {
    const el = ref.current;
    if (!el) {
      onHeightChange(0);
      return;
    }

    const publish = () => onHeightChange(el.offsetHeight);
    publish();

    // The bar wraps to two lines at 375px and one at wider widths, so its height is not a constant
    // and must not be written as one — the 132px chrome constant this fix works around is the
    // cautionary tale. jsdom implements no ResizeObserver, hence the guard rather than an assumed
    // global.
    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver === 'function') {
      observer = new ResizeObserver(publish);
      observer.observe(el);
    }

    return () => {
      observer?.disconnect();
      onHeightChange(0);
    };
  }, [hidden, navVisible, onHeightChange]);

  if (hidden) return null;

  return (
    <div
      ref={ref}
      data-testid="install-banner"
      role="complementary"
      aria-label="Install this app"
      class={`fixed ${navVisible ? 'bottom-20' : 'bottom-0'} left-0 right-0 z-30 flex items-center justify-between gap-3 border-t border-surface-mid bg-surface px-4 py-3 shadow-md`}
    >
      {state === 'installable' ? (
        <span class="text-sm text-text">Install CARii Kereta for a faster, full-screen app.</span>
      ) : (
        <span data-testid="install-ios-hint" class="text-sm text-text">
          Install this app: tap <strong>Share</strong>, then <strong>Add to Home Screen</strong>.
        </span>
      )}

      <div class="flex shrink-0 items-center gap-2">
        {state === 'installable' && (
          <button
            type="button"
            data-testid="install-action"
            onClick={() => void promptInstall()}
            // Sized to the app's own `Button` md (`text-sm px-4 py-2`, ~36px) rather than to the
            // two existing banners, whose `text-xs py-1` controls measure 24px live. This is the
            // primary action of the offer and is meant to be tapped on a phone.
            class="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-on-primary hover:bg-primary-dark"
          >
            Install
          </button>
        )}
        <button
          type="button"
          data-testid="install-dismiss"
          onClick={dismiss}
          aria-label="Dismiss install offer"
          class="rounded-full px-3 py-2 text-sm font-semibold text-text-muted hover:text-text"
        >
          Not now
        </button>
      </div>
    </div>
  );
}
